import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core'
import { BdcPhotoTag } from '@tokeet-frontend/channel-content'
import { BdcContentV2Service } from '@tokeet-frontend/channel-content'
import { Store } from '@ngrx/store'
import { ActionFailed, Destroyable, Toaster, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { BDCPhotoGridCardItem } from '../photo-card/photo-card.interface'
import * as lodash from 'lodash'
import { debounceTime, switchMap } from 'rxjs/operators'
import { MatPaginator, PageEvent } from '@angular/material/paginator'

@Component({
  selector: 'app-photo-grid',
  templateUrl: './photo-grid.component.html',
  styleUrls: ['./photo-grid.component.scss'],
})
export class PhotoGridComponent extends Destroyable implements OnInit, OnChanges {
  @Input() photos: BDCPhotoGridCardItem[]
  @Input() showSelection = true
  @Input() showDelete = true
  @Input() showSetMain = false
  @Input() showTags = true
  @Input() propertyId: number
  @Input() roomId?: number
  @Input() sortable = false
  @ViewChild('paginator') paginator: MatPaginator

  selectedPhotos = new Set<string>()
  availableTags: BdcPhotoTag[] = []

  // Pagination properties
  pageSize = 10
  pageSizeOptions: number[] = [10, 15, 20, 50, 75, 100]
  currentPage = 0
  paginatedPhotos: BDCPhotoGridCardItem[] = []

  @Output() delete = new EventEmitter<string>()
  @Output() setMain = new EventEmitter<string>()

  orderChange = new EventEmitter<string[]>()

  constructor(private contentService: BdcContentV2Service, private store: Store<any>, private toaster: Toaster) {
    super()
  }

  ngOnInit(): void {
    this.loadAvailableTags()
    this.orderChange
      .pipe(
        debounceTime(1500),
        switchMap((ids) => {
          if (this.roomId) {
            return this.contentService.updateRoomPhotoGallery(this.propertyId, this.roomId, { order: ids })
          }
          return this.contentService.updatePropertyPhotoGallery(this.propertyId, { order: ids })
        }),
        untilDestroy(this)
      )
      .subscribe(
        () => {
          this.toaster.success($localize`Photo order updated successfully`)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )

    // Initialize pagination
    this.updatePaginatedPhotos()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['photos'] && this.photos) {
      // Reset to first page when photos change
      this.currentPage = 0
      this.paginator?.firstPage()
      this.updatePaginatedPhotos()
    }
  }

  private loadAvailableTags(): void {
    this.contentService.getAllPhotoTags().subscribe(
      (tags) => {
        this.availableTags = lodash
          .sortBy(tags, (t) => lodash.toLower(t.tag))
          .map((t) => ({ ...t, tag: lodash.capitalize(t.tag) }))
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  isPhotoSelected(photoId: string): boolean {
    return this.selectedPhotos.has(photoId)
  }

  onSelect(photoId: string): void {
    if (this.selectedPhotos.has(photoId)) {
      this.selectedPhotos.delete(photoId)
    } else {
      this.selectedPhotos.add(photoId)
    }
  }

  onDelete(photoId: string): void {
    this.delete.emit(photoId)
  }

  onSetMain(photoId: string): void {
    this.setMain.emit(photoId)
    this.orderChange.emit([photoId, ...this.photos.map((p) => p.photo_id).filter((id) => id !== photoId)])
  }

  onReorder(reorderedPagePhotos: BDCPhotoGridCardItem[]) {
    // Update the current page's photos in the full photos array
    const startIndex = this.currentPage * this.pageSize
    const updatedPhotos = [...this.photos]

    // Replace the current page's photos with the reordered ones
    for (let i = 0; i < reorderedPagePhotos.length; i++) {
      updatedPhotos[startIndex + i] = reorderedPagePhotos[i]
    }

    // Emit all photo IDs in the new order
    const allIds = updatedPhotos.map((t) => t.photo_id)
    this.orderChange.emit(allIds)
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex
    this.pageSize = event.pageSize
    this.updatePaginatedPhotos()
  }

  private updatePaginatedPhotos(): void {
    if (!this.photos) {
      this.paginatedPhotos = []
      return
    }

    const startIndex = this.currentPage * this.pageSize
    this.paginatedPhotos = this.photos.slice(startIndex, startIndex + this.pageSize)
  }
}
